import { BreadSearchInstitution } from "breadcommon";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";

// The Add Account dialog's bank search is slow the first time because its
// Cloud Functions have to start up (~3s each). These let the app start them,
// and fetch the bank grid, as soon as the user looks likely to link an account,
// so the dialog is ready by the time they open it.

// The bank grid barely changes, so one fetch serves the whole session.
let initialInstitutions: BreadSearchInstitution[] | null = null;
let initialInstitutionsRequest: Promise<BreadSearchInstitution[]> | null = null;

// A started function shuts down after sitting idle for a while, so a warm-up
// only lasts so long. Re-warming more often than this would just add calls.
const SEARCH_WARM_UP_INTERVAL_MS = 5 * 60 * 1000;
let lastSearchWarmUp = -Infinity;

/** Starts the bank search's functions and fetches the bank grid. */
export function prefetchInstitutionSearch(): void {
  loadInitialInstitutions().catch(() => {
    // The dialog fetches it again, and reports failures, when it opens.
  });
  warmUpInstitutionSearch();
}

/** The bank grid, if it has already been fetched this session. */
export function getLoadedInitialInstitutions():
  | BreadSearchInstitution[]
  | null {
  return initialInstitutions;
}

/**
 * Fetches the bank grid, sharing one request between callers. A failed or
 * unsuccessful fetch isn't kept, so the next caller tries again.
 */
export function loadInitialInstitutions(): Promise<BreadSearchInstitution[]> {
  if (initialInstitutions !== null) {
    return Promise.resolve(initialInstitutions);
  }
  initialInstitutionsRequest ??= callFirebaseFunction(
    "GetInitialInstitutions",
    {},
  )
    .then((result) => {
      if (!result.data.success || !result.data.institutions) {
        throw new Error(result.data.error_msg ?? "No institutions returned");
      }
      initialInstitutions = result.data.institutions;
      return initialInstitutions;
    })
    .finally(() => {
      initialInstitutionsRequest = null;
    });
  return initialInstitutionsRequest;
}

/** Starts the search function so the user's first search doesn't wait on it. */
export function warmUpInstitutionSearch(): void {
  const now = Date.now();
  if (now - lastSearchWarmUp < SEARCH_WARM_UP_INTERVAL_MS) return;
  lastSearchWarmUp = now;
  callFirebaseFunction("SearchInstitutions", { query: "", warmUp: true }).catch(
    () => {
      // Only a head start; a real search still works without it.
      lastSearchWarmUp = -Infinity;
    },
  );
}
